import { useCallback, useEffect, useRef, useState } from 'react'
import { maskDestination } from '@/utils/mask'

const ACCOUNT_API_BASE =
  import.meta.env.VITE_ACCOUNT_API_BASE || 'https://alelo-sit-api.ysec-technology.workers.dev'
const RESEND_SECONDS = 30

export { RESEND_SECONDS }

export type MagicLinkChannel = 'email' | 'sms'

type StartResponse = {
  ok?: boolean
  challenge_id?: string
  /** Destino parcialmente mascarado devolvido pelo Worker (quando existe). */
  destination?: string
}

type VerifyResponse = {
  ok?: boolean
  redirect?: string
  error?: string
}

async function post<T>(path: string, body: unknown): Promise<{ ok: boolean; data: Partial<T> }> {
  const res = await fetch(`${ACCOUNT_API_BASE}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  const data = (await res.json().catch(() => ({}))) as Partial<T>
  return { ok: res.ok, data }
}

/**
 * Login sem senha (S3) — link de acesso por e-mail ou código por SMS.
 * O envio passa pelo Worker `account-api`; o código digitado é verificado
 * e o Worker devolve o redirect para a sessão.
 */
export function useMagicLinkStart() {
  const [sending, setSending] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [sent, setSent] = useState(false)
  const [channel, setChannel] = useState<MagicLinkChannel>()
  const [challengeId, setChallengeId] = useState<string>()
  const [destination, setDestination] = useState<string>()
  const [error, setError] = useState<string>()
  const [redirect, setRedirect] = useState<string>()
  const [resendDisabled, setResendDisabled] = useState(false)
  const [resendSeconds, setResendSeconds] = useState(0)
  const [resendNotice, setResendNotice] = useState(false)
  const timerRef = useRef<number | null>(null)

  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearInterval(timerRef.current)
    },
    [],
  )

  const startTimer = useCallback((announce = false) => {
    setResendDisabled(true)
    setResendSeconds(RESEND_SECONDS)
    if (announce) setResendNotice(true)
    if (timerRef.current !== null) window.clearInterval(timerRef.current)
    timerRef.current = window.setInterval(() => {
      setResendSeconds((seconds) => {
        if (seconds <= 1) {
          if (timerRef.current !== null) window.clearInterval(timerRef.current)
          timerRef.current = null
          setResendDisabled(false)
          return 0
        }
        return seconds - 1
      })
    }, 1000)
  }, [])

  const start = useCallback(
    async (cpfDigits: string, nextChannel: MagicLinkChannel) => {
      setError(undefined)
      setResendNotice(false)
      setSending(true)
      try {
        const { ok, data } = await post<StartResponse>('/api/passwordless/start', {
          cpf: cpfDigits,
          channel: nextChannel,
        })
        if (!ok) throw new Error(`start_failed`)
        if (nextChannel === 'sms' && !data.challenge_id) throw new Error('no_challenge')
        setChannel(nextChannel)
        if (data.challenge_id) setChallengeId(data.challenge_id)
        if (data.destination) setDestination(maskDestination(data.destination))
        setSent(true)
        if (nextChannel === 'sms') startTimer()
      } catch {
        setError('failed')
        setChannel(undefined)
      } finally {
        setSending(false)
      }
    },
    [startTimer],
  )

  const verify = useCallback(
    async (otp: string): Promise<boolean> => {
      setError(undefined)
      if (!challengeId) {
        setError('invalid_code')
        return false
      }
      setVerifying(true)
      try {
        const { ok, data } = await post<VerifyResponse>('/api/passwordless/verify', {
          challenge_id: challengeId,
          otp,
        })
        if (!ok || !data.ok || !data.redirect) {
          setError('invalid_code')
          return false
        }
        setRedirect(data.redirect)
        window.location.assign(data.redirect)
        return true
      } catch {
        setError('invalid_code')
        return false
      } finally {
        setVerifying(false)
      }
    },
    [challengeId],
  )

  const resend = useCallback(
    async (cpfDigits: string) => {
      if (resendDisabled || sending) return
      setError(undefined)
      setSending(true)
      try {
        const { ok, data } = await post<StartResponse>(
          '/api/passwordless/start',
          { cpf: cpfDigits, channel: 'sms' },
        )
        if (!ok) throw new Error('start_failed')
        if (data.challenge_id) setChallengeId(data.challenge_id)
        startTimer(true)
      } catch {
        setError('failed')
      } finally {
        setSending(false)
      }
    },
    [resendDisabled, sending, startTimer],
  )

  const reset = useCallback(() => {
    setSent(false)
    setError(undefined)
    setChannel(undefined)
    setChallengeId(undefined)
    setDestination(undefined)
    setRedirect(undefined)
    setResendNotice(false)
    setResendDisabled(false)
    setResendSeconds(0)
    if (timerRef.current !== null) window.clearInterval(timerRef.current)
    timerRef.current = null
  }, [])

  return {
    start,
    verify,
    resend,
    reset,
    sending,
    verifying,
    sent,
    channel,
    destination,
    error,
    redirect,
    resendDisabled,
    resendSeconds,
    resendNotice,
  }
}
